// Project detail (MUI, /projects/:id). Presentational twin of dashboard/ProjectDetailPage.tsx.
// Same data/hooks: registry record + aaron board + activity + files; tabs via ?tab=.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useAgent, useAgentSnapshot } from "../../agent/AgentProvider";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Tab,
  Tabs,
} from "@mui/material";
import {
  getProject,
  saveProject,
  deleteProjectRecord,
  projectActivity,
  getAaronProject,
  listFiles,
  getProjectHeartbeat,
  getPresence,
  listFollowups,
  type Project,
  type ProjectDetail,
  type StoreObject,
  type ProjectHeartbeatResponse,
  type ProjectFollowup,
} from "../../lib/brain-api";
import type { Thought } from "../../lib/brain-types";
import { useProfile } from "../../lib/profile";
import { brainTokens } from "../../design/brainTheme";
import { ConflictWarningBanner } from "../../components/ConflictWarning";
import { StatusPill, Icon, EmptyState, projectStatusMeta, type PillTone } from "../../mui";
import { ProjectStatusPipeline } from "./ProjectStatusPipeline";
import { ScopeDocsTab } from "./docs/ScopeDocsTab";
import { ProjectOverviewTab } from "./overview/ProjectOverviewTab";
import { ProjectBoardTab } from "./board/ProjectBoardTab";
import { ProjectActivityFeed, captureCatchUpSince, readVisitSince } from "./activity/ProjectActivityFeed";

const c = brainTokens.color;

/** Canonical tabs after the page-frame redesign. */
type TabKey = "overview" | "docs" | "board" | "activity";

/** Old bookmarks → board (or docs for plan). */
function normalizeTab(raw: string | null): TabKey {
  const t = (raw ?? "overview").toLowerCase();
  if (t === "plan") return "docs";
  if (t === "actions" || t === "jira" || t === "decisions" || t === "files") return "board";
  if (t === "overview" || t === "docs" || t === "board" || t === "activity") return t;
  return "overview";
}

/** Signal-band fact chip: white mini-card, mono uppercase label + optional value. */
function FactChip({ label, value }: { label: string; value?: string }) {
  return (
    <Box
      component="span"
      sx={{
        fontFamily: brainTokens.font.mono,
        fontSize: 10,
        fontWeight: 500,
        letterSpacing: ".1em",
        background: c.white,
        border: `1px solid ${c.tealTintBorder}`,
        borderRadius: "6px",
        padding: "3px 8px",
        display: "inline-flex",
        gap: "5px",
      }}
    >
      <Box component="span" sx={{ color: c.muted }}>
        {label}
      </Box>
      {value != null && <Box component="span" sx={{ color: c.ink }}>{value}</Box>}
    </Box>
  );
}

export function ProjectDetailPageMui() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { profile } = useProfile();
  const isAdmin = profile?.role === "admin";
  const [searchParams, setSearchParams] = useSearchParams();

  const todayNZ = useMemo(
    () => new Intl.DateTimeFormat("en-CA", { timeZone: "Pacific/Auckland", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date()),
    [],
  );

  const [detail, setDetail] = useState<ProjectDetail | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [draft, setDraft] = useState<Project | null>(null);
  const [activity, setActivity] = useState<Thought[]>([]);
  const [files, setFiles] = useState<StoreObject[]>([]);
  const [heartbeat, setHeartbeat] = useState<ProjectHeartbeatResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  /** Header Catch me up → Activity tab with since-filter on (Screen 5 / F1).
   * Stamp + sinceIso live here so leaving/returning to Activity never re-stamps. */
  const [activityCatchUp, setActivityCatchUp] = useState(false);
  const [activityCatchUpSince, setActivityCatchUpSince] = useState(() => readVisitSince(id));
  const catchUpStampedRef = useRef(false);
  const [conflictHolders, setConflictHolders] = useState<
    Array<{ displayName: string; sinceLabel: string; unpushed: boolean; heldDays?: number }>
  >([]);
  const [followups, setFollowups] = useState<ProjectFollowup[]>([]);

  /** Bumped per load so a slow response from a PREVIOUS project can't paint over
   *  the current one — the sections now land independently, over ~2s. */
  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    const alive = () => loadSeq.current === seq;
    setLoading(true);

    // All seven still go out at once — what changed is what we WAIT for. The
    // page shell needs only the two record calls (~260ms); heartbeat is 1.8–2.6s
    // and used to hold the entire page, spinner and all, behind itself. Each
    // remaining section now fills itself in as it lands.
    const detailP = getAaronProject(id, todayNZ);
    const projectP = getProject(id);
    const activityP = projectActivity(id, 500);
    const filesP = listFiles(`projects/${id}/`);
    const heartbeatP = getProjectHeartbeat(id);
    const presenceP = getPresence();
    const followupsP = listFollowups({ project: id, status: "open" });

    void activityP.then((v) => alive() && setActivity(v)).catch(() => {});
    void filesP.then((v) => alive() && setFiles(v)).catch(() => {});
    void heartbeatP.then((v) => alive() && setHeartbeat(v)).catch(() => {});
    void followupsP.then((v) => alive() && setFollowups(v)).catch(() => {});
    void presenceP
      .then((pres) => {
        if (!alive() || !profile?.userId) return;
        const scope = `project:${id}`;
        // Exclude my own sessions — match on holder AND resolved slug (my agent's
        // sessions resolve to my slug, not my userId). Guarded on profile being
        // loaded so an unloaded profile can't flag me against myself.
        const mineIds = new Set([profile.userId, profile.personSlug].filter(Boolean));
        setConflictHolders(
          pres.sessions
            .filter(
              (s) =>
                s.scope === scope &&
                !mineIds.has(s.holder) &&
                !(s.slug && mineIds.has(s.slug)) &&
                (s.state === "active" || s.state === "held-unpushed")
            )
            .map((s) => ({
              displayName: s.displayName || s.holderLabel || s.holder,
              sinceLabel: new Date(s.startedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
              unpushed: s.state === "held-unpushed",
              heldDays: Math.max(0, Math.floor((Date.now() - s.startedAt) / 86400000)),
            }))
        );
      })
      .catch(() => {});

    const [d, p] = await Promise.allSettled([detailP, projectP]);
    if (!alive()) return;
    if (d.status === "fulfilled") setDetail(d.value);
    if (p.status === "fulfilled") {
      setProject(p.value);
      setDraft(p.value);
    }
    if (d.status !== "fulfilled" && p.status !== "fulfilled") {
      enqueueSnackbar("Failed to load project.", { variant: "error" });
    }
    setLoading(false);
  }, [id, todayNZ, enqueueSnackbar, profile?.userId, profile?.personSlug]);
  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      const saved = await saveProject(draft);
      setProject(saved);
      setDraft(saved);
      setEditing(false);
      enqueueSnackbar("Saved.", { variant: "success" });
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Save failed.", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await deleteProjectRecord(id);
      enqueueSnackbar("Project deleted.", { variant: "info" });
      navigate("/projects");
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Delete failed.", { variant: "error" });
      setBusy(false);
    }
  };

  const rawTab = searchParams.get("tab");
  const activeTab = normalizeTab(rawTab);
  const goTab = (tab: TabKey) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    setSearchParams(next, { replace: true });
  };

  // Catch-up since floor: stamp once in the parent; remounting Activity must not re-stamp.
  useEffect(() => {
    setActivityCatchUp(false);
    setActivityCatchUpSince(readVisitSince(id));
    catchUpStampedRef.current = false;
  }, [id]);

  const enableActivityCatchUp = useCallback(() => {
    if (!catchUpStampedRef.current) {
      setActivityCatchUpSince(captureCatchUpSince(id));
      catchUpStampedRef.current = true;
    }
    setActivityCatchUp(true);
  }, [id]);

  const onActivityCatchUpChange = useCallback(
    (active: boolean) => {
      if (active) enableActivityCatchUp();
      else setActivityCatchUp(false);
    },
    [enableActivityCatchUp],
  );
  // Rewrite legacy ?tab= bookmarks (actions/jira/decisions/files/plan) to the
  // canonical key so the URL matches the strip.
  useEffect(() => {
    if (!rawTab) return;
    const normalized = normalizeTab(rawTab);
    if (rawTab.toLowerCase() !== normalized) {
      const next = new URLSearchParams(searchParams);
      next.set("tab", normalized);
      setSearchParams(next, { replace: true });
    }
  }, [rawTab, searchParams, setSearchParams]);

  // Agent partner contract: register the entity (with its NAME, which the URL
  // floor can't know) + a compact on-screen snapshot (ids + numbers only).
  const { setPageContext } = useAgent();
  const agentEntityName = detail?.project?.name ?? project?.name ?? id;
  useEffect(() => {
    if (!id) return;
    setPageContext({
      route: `/projects/${id}`,
      entity: { type: "project", id, name: agentEntityName },
      tab: activeTab,
    });
    return () => setPageContext(null);
  }, [id, agentEntityName, activeTab, setPageContext]);
  useAgentSnapshot(
    detail || project
      ? {
          tab: activeTab,
          status: project?.status ?? detail?.project?.status,
          ...(project?.blocked ? { blocked: true } : {}),
          openActions: (detail?.actions ?? [])
            .filter((a) => a.status !== "done" && a.status !== "dismissed")
            .slice(0, 10)
            .map((a) => ({ id: a.id, text: a.text, status: a.status, ...(a.isGate ? { gate: true } : {}) })),
          followups: followups.slice(0, 5).map((f) => ({ id: f.id, text: f.text })),
          fileCount: files.length,
        }
      : null,
  );
  // Followups stay on standup surfaces; Overview no longer flashes pickup rows.

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={24} sx={{ color: c.teal }} />
      </Box>
    );
  }

  const bp = detail?.project;
  if (!bp && !project) {
    return (
      <EmptyState icon="alert-triangle" title="Project not found">
        It may have been deleted.
      </EmptyState>
    );
  }

  const name = bp?.name ?? project?.name ?? id;
  const customerId = bp?.customerId ?? project?.customer;
  const customerName = bp?.customerName ?? customerId;
  const owner = bp?.owner ?? project?.owner;
  const jiraKey = bp?.jiraTarget ?? project?.links.jira_epic;
  const rollups = heartbeat?.rollups;
  const progress = heartbeat?.progress;
  const weeklyTrend = heartbeat?.weeklyTrend;
  const st = projectStatusMeta(project?.blocked ? "blocked" : (project?.status ?? bp?.status ?? ""));

  const boardIssues = heartbeat?.issues ?? [];
  const realFiles = files.filter((f) => !f.key.endsWith("/.keep"));
  const projectPrefix = `projects/${bp?.projectId ?? id}/`;

  const set = (patch: Partial<Project>) => setDraft((d) => (d ? { ...d, ...patch } : d));

  // ponytail: Plan tab retired (Isaac 2026-08-12) — plan.md is a page in Docs.
  // Actions/Jira/Decisions/Files tabs fold into Board; legacy ?tab= URLs redirect.
  const boardOpenCount = rollups?.open ?? Math.max(0, (rollups?.total ?? 0) - (rollups?.done ?? 0));
  const docsCount = realFiles.filter((f) => /\.(html?|md)$/i.test(f.key)).length;
  const tabDefs: { key: TabKey; label: string; count?: number }[] = [
    { key: "overview", label: "Overview" },
    { key: "docs", label: "Docs", count: docsCount || undefined },
    { key: "board", label: "Board", count: boardOpenCount || undefined },
    { key: "activity", label: "Activity" },
  ];

  const doneN = progress?.done ?? rollups?.done ?? 0;
  const totalN = progress?.total ?? rollups?.total ?? 0;
  const lead = project?.assignee ?? owner ?? "—";

  return (
    <Box sx={{ maxWidth: 1180 }}>
      {/* Row A — breadcrumb + actions */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", minHeight: 26 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "7px", fontSize: 12.5, color: c.muted2, minWidth: 0 }}>
          <Box
            component={RouterLink}
            to="/projects"
            sx={{ color: c.muted, textDecoration: "none", "&:hover": { color: c.ink } }}
          >
            Projects
          </Box>
          <Box component="span" sx={{ color: c.placeholder }}>
            ›
          </Box>
          <Box component="span" sx={{ color: c.muted2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {name}
          </Box>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          variant="text"
          onClick={() => {
            enableActivityCatchUp();
            goTab("activity");
          }}
          startIcon={<Icon name="clock" size={15} stroke={1.8} />}
          sx={{ color: c.ink3, fontSize: 12.5, minHeight: 30, px: 1 }}
        >
          Catch me up
        </Button>
        {isAdmin && project && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              goTab("overview");
              setEditing((v) => !v);
            }}
            startIcon={<Icon name="edit" size={15} stroke={1.8} />}
            sx={{ fontSize: 12.5, minHeight: 30 }}
          >
            {editing ? "Editing…" : "Edit record"}
          </Button>
        )}
      </Box>

      <ConflictWarningBanner holders={conflictHolders} />

      {/* Signal band — status + key facts + progress, tinted */}
      <Box
        sx={{
          background: c.tealTint2,
          border: `1px solid ${c.tealTintBorder}`,
          borderRadius: 0,
          mt: "14px",
          padding: "20px 32px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "32px",
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box
            component="h1"
            sx={{
              m: 0,
              fontSize: 28,
              fontWeight: 700,
              letterSpacing: "-.02em",
              lineHeight: 1.2,
              color: c.ink,
            }}
          >
            {name}
          </Box>

          {/* Fact chips */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mt: "12px", flexWrap: "wrap" }}>
            <FactChip label="LEAD" value={lead} />
            <FactChip label="OWNER" value={owner ?? "—"} />
            {(project?.team || project?.kind) && (
              <FactChip label={(project?.team ?? project?.kind ?? "").toUpperCase()} />
            )}
            {project?.priority && (
              <Box
                component="span"
                sx={{
                  fontFamily: brainTokens.font.mono,
                  fontSize: 10,
                  fontWeight: 500,
                  letterSpacing: ".1em",
                  color: c.warnText,
                  background: c.warnBg,
                  border: `1px solid ${c.warnBorder}`,
                  borderRadius: "6px",
                  padding: "3px 8px",
                }}
              >
                {project.priority}
              </Box>
            )}
            {project?.blocked && (
              <StatusPill
                label={project.blockedReason ? `Blocked · ${project.blockedReason}` : "Blocked · needs a reason"}
                tone="danger"
                dot
              />
            )}
          </Box>

          {totalN > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mt: "12px" }}>
              <Box
                sx={{
                  width: 110,
                  height: 6,
                  borderRadius: "999px",
                  background: c.white,
                  border: `1px solid ${c.tealTintBorder}`,
                  overflow: "hidden",
                  flex: "none",
                }}
              >
                <Box sx={{ height: "100%", width: `${Math.min(100, (doneN / Math.max(totalN, 1)) * 100)}%`, background: c.teal }} />
              </Box>
              <Box component="span" sx={{ fontSize: 12.5, color: c.ink4 }}>
                {doneN} of {totalN} done
              </Box>
              {weeklyTrend && (
                <Box
                  component="span"
                  sx={{ fontSize: 12.5, fontWeight: 500, color: weeklyTrend.delta >= 0 ? c.successText : c.dangerText }}
                >
                  {weeklyTrend.delta >= 0 ? "+" : ""}
                  {weeklyTrend.delta} this week
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ flex: "none" }}>
          {project ? (
            <ProjectStatusPipeline
              project={project}
              canEdit={isAdmin}
              onChanged={(p) => {
                setProject(p);
                setDraft(p);
              }}
            />
          ) : (
            <StatusPill label={st.word} tone={st.tone} dot />
          )}
        </Box>
      </Box>

      {/* Tab strip — Overview · Docs n · Board n · Activity */}
      <Tabs value={activeTab} onChange={(_, v) => goTab(v as TabKey)} sx={{ mb: "20px" }}>
        {tabDefs.map((t) => (
          <Tab
            key={t.key}
            value={t.key}
            disableRipple
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
                <Box component="span">{t.label}</Box>
                {typeof t.count === "number" && (
                  <Box
                    component="span"
                    sx={{
                      fontFamily: brainTokens.font.mono,
                      fontSize: 10.5,
                      color: c.muted2,
                      background: c.panelBg2,
                      borderRadius: `${brainTokens.radius.pill}px`,
                      padding: "1px 6px",
                    }}
                  >
                    {t.count}
                  </Box>
                )}
              </Box>
            }
          />
        ))}
      </Tabs>

      {activeTab === "overview" && (
        <ProjectOverviewTab
          projectId={id}
          project={project}
          draft={draft}
          editing={editing}
          canEdit={!!isAdmin && !!project}
          busy={busy}
          heartbeat={heartbeat}
          boardIssues={boardIssues}
          customerId={customerId}
          customerName={customerName}
          owner={owner}
          jiraKey={jiraKey}
          fileCount={realFiles.length}
          atomCount={activity.length}
          onGoBoard={() => goTab("board")}
          onGoActivity={() => goTab("activity")}
          onEdit={() => setEditing(true)}
          onCancel={() => {
            setDraft(project);
            setEditing(false);
          }}
          onSave={() => void save()}
          onDelete={() => setDeleteOpen(true)}
          setDraft={set}
          onScopesChanged={() => void load()}
        />
      )}

      {activeTab === "docs" && (
        <ScopeDocsTab
          prefix={projectPrefix}
          label={name}
          openKey={searchParams.get("doc")}
          onOpenKey={(rel) => {
            const next = new URLSearchParams(searchParams);
            if (rel) next.set("doc", rel);
            else next.delete("doc");
            setSearchParams(next, { replace: true });
          }}
        />
      )}

      {activeTab === "board" && <ProjectBoardTab key={id} projectId={id} isAdmin={!!isAdmin} />}

      {activeTab === "activity" && (
        <ProjectActivityFeed
          projectId={id}
          catchUpActive={activityCatchUp}
          sinceIso={activityCatchUpSince}
          onCatchUpChange={onActivityCatchUpChange}
        />
      )}

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete project &ldquo;{id}&rdquo;?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13.5 }}>
            Its atoms remain but the facet stops validating.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} sx={{ color: c.ink3 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={busy}
            onClick={() => {
              setDeleteOpen(false);
              void remove();
            }}
            sx={{ background: c.dangerText2, "&:hover": { background: c.dangerText } }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
